import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { apiClient } from "@/api/ApiClient";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<{
    type?: string;
    tokens?: number;
    planType?: string;
    userTokens?: number;
    isPremium?: boolean;
  } | null>(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setError(t("payment.no_session_id"));
      setVerifying(false);
      return;
    }

    let isMounted = true; // Flag to prevent multiple calls

    const verifyPayment = async () => {
      try {
        const response = await apiClient.get(
          `/billing/verify-checkout-session?session_id=${sessionId}`
        );

        if (!isMounted) return; // Component unmounted, don't update state

        if (response.success && response.paid) {
          setVerified(true);
          setPaymentData({
            type: response.type,
            tokens: response.tokens,
            planType: response.planType,
            userTokens: response.userTokens,
            isPremium: response.isPremium,
          });

          // Show success toast only once
          if (response.type === "token_purchase") {
            toast({
              title: t("payment.success_title"),
              description: t("payment.tokens_added", { tokens: response.tokens }),
            });
          } else if (response.type === "subscription") {
            toast({
              title: t("payment.subscription_activated_title"),
              description: t("payment.subscription_active"),
            });
          }

          // Trigger token/premium update event
          try {
            window.dispatchEvent(new CustomEvent("tokens-updated"));
            window.dispatchEvent(new CustomEvent("premium-updated"));
          } catch (e) {
            console.warn("Failed to dispatch update events:", e);
          }

          // Redirect after 3 seconds
          setTimeout(() => {
            if (isMounted) {
              navigate("/");
            }
          }, 3000);
        } else {
          setError(response.message || t("payment.verification_failed"));
        }
      } catch (err: any) {
        if (!isMounted) return;
        console.error("Error verifying payment:", err);
        setError(err.message || t("payment.verify_error"));
      } finally {
        if (isMounted) {
          setVerifying(false);
        }
      }
    };

    verifyPayment();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [sessionId]); // Remove toast and t from dependencies to prevent re-runs

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t("payment.verifying")}</h2>
          <p className="text-muted-foreground">
            {t("payment.verifying_wait")}
          </p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t("payment.verification_failed_title")}</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate("/")}>{t("payment.go_home")}</Button>
        </Card>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t("payment.success_title")}</h2>
          {paymentData?.type === "token_purchase" && paymentData.tokens && (
            <div className="mb-4">
              <p className="text-muted-foreground mb-2">
                {t("payment.tokens_received_prefix")} <strong>{paymentData.tokens}</strong> {t("payment.tokens_received_suffix")}
              </p>
              {paymentData.userTokens !== undefined && (
                <p className="text-sm text-muted-foreground">
                  {t("payment.current_balance_prefix")} <strong>{paymentData.userTokens}</strong> {t("payment.current_balance_suffix")}
                </p>
              )}
            </div>
          )}
          {paymentData?.type === "subscription" && (
            <div className="mb-4">
              <p className="text-muted-foreground mb-2">
                {t("payment.subscription_active_title")}
              </p>
              {paymentData.planType && (
                <p className="text-sm text-muted-foreground">
                  {t("payment.plan")}: <strong>{paymentData.planType === "monthly" ? t("premium_modal.monthly") : t("premium_modal.yearly")}</strong>
                </p>
              )}
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-6">
            {t("payment.redirecting")}
          </p>
          <Button onClick={() => navigate("/")}>{t("payment.go_home_now")}</Button>
        </Card>
      </div>
    );
  }

  return null;
};

